import { Link } from "react-router-dom";
import { Truck, Clock, ShieldCheck, ArrowRight, Globe, ShoppingBag, Plane, Warehouse, MapPin, Scale, FileCheck, ExternalLink, UserPlus } from "lucide-react";

const BEC_SIGNUP = "https://bec.tasoko.com/login";
const BEC_WEBSITE = "https://cbcja.com/";

const steps = [
  { n: "01", icon: UserPlus, t: "Sign Up Free", d: "Create your BEC Couriers account and get your personal Miami shipping address." },
  { n: "02", icon: ShoppingBag, t: "Shop Online", d: "Buy from Amazon, Walmart, eBay, SHEIN, or any US online store and ship to your Miami address." },
  { n: "03", icon: Warehouse, t: "We Receive in Miami", d: "Your packages arrive at our Miami warehouse where we consolidate and prep them for shipping." },
  { n: "04", icon: Plane, t: "Air Freight to Jamaica", d: "We ship to Jamaica, clear customs on your behalf, and process your duties." },
  { n: "05", icon: MapPin, t: "Pick Up or Delivery", d: "Collect your package at our Jamaica locations, or arrange home delivery." },
];

const features = [
  { icon: Globe, t: "Shop the world", d: "Use your Miami address to order from any US online store." },
  { icon: Scale, t: "Charged by pound", d: "Transparent per-pound pricing. No hidden fees, ever." },
  { icon: FileCheck, t: "Customs handled", d: "We pre-clear and process duties so you don't have to deal with paperwork." },
  { icon: Clock, t: "Fast turnaround", d: "Weekly air freight from Miami to Jamaica with reliable schedules." },
  { icon: ShieldCheck, t: "Secure & insured", d: "Every parcel is tracked and protected from warehouse to your hands." },
  { icon: Truck, t: "Pickup options", d: "Collect at our Jamaica locations or have it delivered to your door." },
];

export default function Courier() {
  return (
    <div data-testid="courier-page">
      {/* HERO */}
      <section className="relative bg-[#1E2A78] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 amp-grid-overlay opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#FFD700]/15 border border-[#FFD700]/40 text-[#FFD700] text-xs font-semibold uppercase tracking-wider mb-6">
              <Truck className="h-3.5 w-3.5" /> Powered by BEC Couriers — Jamaica
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight leading-[1.05]">
              Shop the US. <br />
              <span className="text-[#FFD700]">Ship to Jamaica.</span>
            </h1>
            <p className="mt-6 text-slate-200 text-lg leading-relaxed max-w-xl">
              Sign up for our Jamaican courier service and get your own Miami shipping address. Order from any US online store — we receive, ship, clear customs, and deliver in Jamaica.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={BEC_SIGNUP}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="courier-signup-btn"
                className="px-6 py-3 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] inline-flex items-center gap-2 transition-colors"
              >
                <UserPlus className="h-4 w-4" /> Sign Up Free <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={BEC_WEBSITE}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="courier-website-btn"
                className="px-6 py-3 bg-white/10 border border-white/20 text-white font-semibold rounded-sm hover:bg-white/20 inline-flex items-center gap-2 transition-colors"
              >
                Visit BEC Couriers <ExternalLink className="h-4 w-4" />
              </a>
            </div>

            <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center gap-4 text-xs text-slate-300">
              <div className="inline-flex items-center gap-2"><Scale className="h-3.5 w-3.5 text-[#FFD700]" /> Charged by pound</div>
              <div className="inline-flex items-center gap-2"><FileCheck className="h-3.5 w-3.5 text-[#FFD700]" /> Duties handled</div>
              <div className="inline-flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-[#FFD700]" /> JA pickup or delivery</div>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-sm p-8 backdrop-blur">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#FFD700] mb-3">Your new Miami address</div>
            <div className="bg-[#0F172A] border border-white/10 rounded-sm p-5 font-mono text-sm leading-relaxed">
              <div className="text-[#FFD700]">[Your Name] — BEC#[YourID]</div>
              <div className="text-slate-200 mt-1">BEC Couriers Warehouse</div>
              <div className="text-slate-200">Miami, FL — USA</div>
              <div className="text-slate-400 mt-3 text-xs">(Full address provided after sign-up)</div>
            </div>
            <p className="mt-5 text-sm text-slate-300 leading-relaxed">
              Use this address as your shipping address on Amazon, eBay, Walmart, SHEIN, Best Buy, and thousands of other US retailers.
            </p>
            <a
              href={BEC_SIGNUP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="courier-signup-hero-card"
              className="mt-6 w-full px-4 py-3 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] inline-flex items-center justify-center gap-2"
            >
              Get My Free Account <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#1E2A78] mb-3">How It Works</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight">
              From your cart in the US <br /><span className="text-[#1E2A78]">to your hands in Jamaica.</span>
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Five simple steps — managed end-to-end by BEC Couriers, our sister company in Jamaica.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200">
            {steps.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.n} className="bg-white p-6 group hover:bg-[#1E2A78] hover:text-white transition-colors" data-testid={`courier-step-${s.n}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="font-mono text-[#FFD700] text-sm font-bold">{s.n}</div>
                    <Icon className="h-5 w-5 text-[#1E2A78] group-hover:text-[#FFD700]" />
                  </div>
                  <h3 className="text-lg font-display font-bold mb-2">{s.t}</h3>
                  <p className="text-sm leading-relaxed text-slate-600 group-hover:text-slate-200">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 bg-[#F1F5F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight mb-12 max-w-2xl">
            Why Jamaicans choose <span className="text-[#1E2A78]">BEC Couriers.</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-white p-8" data-testid={`courier-feature-${i}`}>
                  <div className="h-10 w-10 rounded-sm bg-[#FFD700]/20 border border-[#FFD700] flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-[#1E2A78]" />
                  </div>
                  <h3 className="text-lg font-display font-bold">{f.t}</h3>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed">{f.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING / RATES NOTE */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#0F172A] text-white rounded-sm p-8 sm:p-12 grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#FFD700] mb-3">Pricing</div>
              <h3 className="text-2xl sm:text-3xl font-display font-extrabold tracking-tight">Simple per-pound pricing + customs duties.</h3>
              <p className="mt-4 text-slate-300 leading-relaxed">
                Your shipment is charged by weight (in pounds) plus any applicable Jamaican customs duties. No subscription fees, no monthly minimums — pay only for what you ship.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-slate-300">
                <li className="flex gap-2"><span className="text-[#FFD700]">✓</span> Free sign-up & free Miami address</li>
                <li className="flex gap-2"><span className="text-[#FFD700]">✓</span> No hidden fees</li>
                <li className="flex gap-2"><span className="text-[#FFD700]">✓</span> Duties calculated transparently</li>
                <li className="flex gap-2"><span className="text-[#FFD700]">✓</span> Pickup at JA locations or arranged delivery</li>
              </ul>
            </div>
            <div className="flex flex-col gap-3 justify-center">
              <a
                href={BEC_SIGNUP}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="courier-pricing-signup"
                className="px-6 py-3 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] inline-flex items-center justify-center gap-2"
              >
                <UserPlus className="h-4 w-4" /> Sign Up Now
              </a>
              <a
                href={BEC_WEBSITE}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="courier-pricing-rates"
                className="px-6 py-3 bg-white/10 border border-white/20 text-white font-semibold rounded-sm hover:bg-white/20 inline-flex items-center justify-center gap-2"
              >
                See Rates <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ-style what to shop */}
      <section className="py-20 bg-[#F1F5F9]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-display font-extrabold mb-8 text-[#0F172A]">What you can shop & ship</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {[
              { t: "Electronics", d: "Phones, laptops, tablets, accessories." },
              { t: "Clothing & Footwear", d: "Fashion from any US retailer." },
              { t: "Beauty & Personal Care", d: "Cosmetics, hair care, skincare." },
              { t: "Home & Kitchen", d: "Small appliances, home goods." },
              { t: "Auto Parts", d: "Spare parts under our weight limits." },
              { t: "Books, Toys & More", d: "Anything legal for import to Jamaica." },
            ].map((b, i) => (
              <div key={i} className="bg-white p-6" data-testid={`courier-category-${i}`}>
                <div className="font-display font-bold text-[#1E2A78]">{b.t}</div>
                <div className="text-sm text-slate-600 mt-1">{b.d}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-500">
            Restricted items: hazardous goods, certain perishables, firearms/ammunition, illegal goods. Full list available on the BEC Couriers website.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 bg-[#1E2A78] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight">Ready to shop the US?</h2>
          <p className="mt-4 text-slate-200 max-w-2xl mx-auto">Sign up free with BEC Couriers and start shipping to Jamaica today.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a
              href={BEC_SIGNUP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="courier-cta-signup"
              className="px-6 py-3 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] inline-flex items-center gap-2"
            >
              <UserPlus className="h-4 w-4" /> Create Free Account
            </a>
            <a
              href={BEC_WEBSITE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="courier-cta-website"
              className="px-6 py-3 bg-white/10 border border-white/20 font-semibold rounded-sm hover:bg-white/20 inline-flex items-center gap-2"
            >
              Visit cbcja.com <ExternalLink className="h-4 w-4" />
            </a>
            <Link to="/contact" data-testid="courier-cta-contact" className="px-6 py-3 bg-white text-[#1E2A78] font-semibold rounded-sm hover:bg-slate-100">
              Talk to us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
